import React from "react";
import { useTranslation } from "react-i18next";
import { Grid, Typography, Box } from "@mui/material";
import { sectionDefinitions } from "./sectionConfig";
import { useDsokCustomerDetail } from "../data/useDsokCustomerDetail";

/**
 * Resolves which sections to render for the given customer type and renders them
 * with the appropriate mode (read/edit). Sections are filtered by visibleForTyps.
 * Sections are laid out in a responsive grid: 4 per row (lg), 2 per row (md), 1 per row (xs).
 *
 * @param {number} customerType - Effective user/customer type (1=Admin, 2=Partner, 3=Customer user, 7=Type 7)
 * @param {string} customerUrl - Customer URL from route params (for section click log)
 */
function SectionResolver({ customerType, customerUrl }) {
  const { t } = useTranslation();
  const { hasEasyBuy, dsokSettings, customer } = useDsokCustomerDetail();

  const frontendType =
    customer?.frontendType ??
    dsokSettings?.customer?.frontendType ??
    dsokSettings?.frontendType ??
    null;
  const isEasyFood = String(frontendType || "").trim() === "easyFood";

  const sections = sectionDefinitions
    .filter((def) => def.visibleForTyps && def.visibleForTyps.includes(customerType))
    .map((def) => {
      const permission = def.getPermission(customerType);
      return { ...def, mode: permission === "edit" ? "edit" : "read" };
    });

  const filteredSections = sections.filter((section) => {
    if (
      (section.id === "categories" || section.id === "dynamic-components") &&
      (!hasEasyBuy || isEasyFood)
    ) {
      return false;
    }
    return true;
  });

  if (filteredSections.length === 0) {
    return (
      <Box sx={{ py: 3 }}>
        <Typography color="text.secondary">
          {t("dsokCustomerDetail.sections.emptyState")}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} alignItems="stretch" sx={{ mb: 2 }}>
      {filteredSections.map((section) => {
        const SectionComponent = section.component;
        return (
          <Grid item xs={12} md={6} lg={3} key={section.id}>
            <SectionComponent
              sectionId={section.id}
              labelKey={section.labelKey}
              descriptionKey={section.descriptionKey}
              icon={section.icon}
              customerUrl={customerUrl}
              mode={section.mode}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

export default SectionResolver;
